import { formatDistanceToNow } from "date-fns";
import { useJobLogs } from "@/hooks/useJobLogs";
import { useItemJob, useDispatchJob } from "@/hooks/useAgentJobs";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { formatError } from "@/lib/utils";

const EVENT_ICONS: Record<string, string> = {
  job_claimed: ">>",
  worktree_created: "//",
  claude_started: "AI",
  changes_detected: "++",
  no_changes: "--",
  pr_created: "PR",
  failed: "!!",
};

const EVENT_COLORS: Record<string, string> = {
  job_claimed: "bg-yellow-500",
  worktree_created: "bg-blue-500",
  claude_started: "bg-purple-500",
  changes_detected: "bg-green-500",
  no_changes: "bg-muted-foreground",
  pr_created: "bg-green-500",
  failed: "bg-red-500",
};

const PROGRESS_STEPS = [
  { event: "job_claimed", label: "Claimed" },
  { event: "worktree_created", label: "Worktree" },
  { event: "claude_started", label: "AI Working" },
  { event: "changes_detected", label: "Changes" },
  { event: "pr_created", label: "PR" },
];

function ProgressBar({ logs }: { logs: { event: string }[] }) {
  const completedEvents = new Set(logs.map((l) => l.event));
  let currentIdx = -1;
  for (let i = PROGRESS_STEPS.length - 1; i >= 0; i--) {
    if (completedEvents.has(PROGRESS_STEPS[i].event)) {
      currentIdx = i;
      break;
    }
  }

  return (
    <div className="flex items-center gap-1">
      {PROGRESS_STEPS.map((step, i) => (
        <div key={step.event} className="flex items-center gap-1">
          <div
            className={`h-1.5 w-6 rounded-full transition-colors ${
              i <= currentIdx
                ? "bg-primary"
                : "bg-muted-foreground/20"
            }`}
            title={step.label}
          />
          {i < PROGRESS_STEPS.length - 1 && (
            <div className="w-0.5" />
          )}
        </div>
      ))}
      <span className="text-[10px] text-muted-foreground ml-1">
        {currentIdx >= 0 ? PROGRESS_STEPS[currentIdx].label : "Queued"}
      </span>
    </div>
  );
}

export function AgentTimeline({ itemId }: { itemId: string }) {
  const { data: job } = useItemJob(itemId);
  const { data: logs } = useJobLogs(job?.id);
  const dispatch = useDispatchJob();

  if (!job) return null;

  const isRunning = ["pending", "claimed", "running"].includes(job.status);

  const handleRetry = () => {
    dispatch.mutate(
      { item_id: itemId, instructions: job.instructions || undefined },
      {
        onSuccess: () => toast("Job re-dispatched"),
        onError: (err) => toast.error(formatError(err)),
      }
    );
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <h4 className="text-xs font-medium text-muted-foreground">Agent Activity</h4>
        <Badge
          variant="outline"
          className={`text-[10px] ${
            job.status === "running" ? "border-blue-500 text-blue-500 animate-pulse" :
            job.status === "completed" ? "border-green-500 text-green-500" :
            job.status === "failed" ? "border-red-500 text-red-500" :
            "border-yellow-500 text-yellow-500"
          }`}
        >
          {job.status}
        </Badge>
      </div>

      {/* Progress bar for running jobs */}
      {isRunning && logs && (
        <ProgressBar logs={logs} />
      )}

      {/* Error display for failed jobs */}
      {job.status === "failed" && job.error && (
        <div className="rounded-lg border border-red-500/30 bg-red-500/5 px-3 py-2 space-y-1.5">
          <p className="text-xs font-medium text-red-500">Error</p>
          <p className="text-xs text-red-400/90 whitespace-pre-line">{job.error}</p>
          <button
            onClick={handleRetry}
            disabled={dispatch.isPending}
            className="text-xs font-medium text-red-500 hover:text-red-400 transition-colors"
          >
            {dispatch.isPending ? "Retrying..." : "Retry →"}
          </button>
        </div>
      )}

      {logs && logs.length > 0 ? (
        <div className="space-y-1.5 pl-2 border-l border-border">
          {logs.map((log) => (
            <div key={log.id} className="flex items-start gap-2 relative">
              <div className={`w-1.5 h-1.5 rounded-full mt-1.5 -ml-[11px] shrink-0 ${EVENT_COLORS[log.event] ?? "bg-muted-foreground"}`} />
              <div className="min-w-0">
                <span className="text-xs font-mono text-muted-foreground mr-1.5">
                  {EVENT_ICONS[log.event] ?? ".."}
                </span>
                <span className="text-xs">{log.message}</span>
                <span className="text-[10px] text-muted-foreground ml-2">
                  {formatDistanceToNow(new Date(log.created_at), { addSuffix: true })}
                </span>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-xs text-muted-foreground">Waiting for agent activity...</p>
      )}

      {job.pr_url && (
        <a
          href={job.pr_url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-block text-xs text-primary hover:underline"
        >
          View PR →
        </a>
      )}

      {job.summary && (
        <p className="text-xs text-muted-foreground italic">{job.summary}</p>
      )}
    </div>
  );
}
